<template>
  <div class="cash-sources-stat">
    <div class="stat-menu-abc">
      <v-container fluid>
        <stat-top-filter
          :filial-default="[globalFilialId]"
          last-month
          multiple-filial-select
          @applyFilter="applyFilter"
        >
          <template #additional-filter>
            <div class="user-filter-search d-flex align-center">
              <label class="stat-filter-col__label">поиск:</label>
              <v-text-field
                v-model="search"
                style="border-radius: 10px;"
                append-icon="mdi-magnify"
                hide-details
                flat
                solo
              ></v-text-field>
            </div>
          </template>
          <template #afterApply="{ filter }">
            <v-btn
              style="border-radius: 10px;"
              height="48px"
              depressed
              color="#D3D5D9"
              :loading="reportDownloading"
              @click="downloadReport(filter)"
            >
              Выгрузить в Excel
            </v-btn>
          </template>
        </stat-top-filter>

        <loader v-if="loading" />
        <template v-else>
          <div v-if="statisticData.length" class="note mb-5">
            {{ noteText }}
          </div>
          <div class="stat-menu-abc-table">
            <v-data-table
              v-if="statisticData.length"
              fixed-header
              :headers="headers"
              :page="page"
              :items="statisticData"
              :items-per-page="itemsPerPage"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :footer-props="footerProps"
              :server-items-length="itemsLength"
              class="statistic-datatable headers-nowrap orange-header"
              @update:options="loadItems"
            >
              <template #item="{item, index}">
                <tr>
                  <td class="text-center">
                    {{ index + 1 }}
                  </td>
                  <td>{{ item.client.name }}</td>
                  <td class="text-center">
                    {{ item.ordersCount }}
                  </td>
                  <td class="text-center">
                    {{ item.pickupOrdersCount }}
                  </td>
                  <td class="text-center">
                    {{ item.deliveryOrdersCount }}
                  </td>
                  <td class="text-center">
                    {{ item.avgBill | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ item.sumOrders | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ formatDate(item.lastOrderAt) }}
                  </td>
                  <td class="text-center">
                    {{ formatDate(item.client.createdAt) }}
                  </td>
                  <td class="text-center">
                    {{ item.client.formattedPhone }}
                  </td>
                  <td class="text-center">
                    <v-tooltip v-if="canBanSMS" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" class="d-inline-block" v-on="on">
                          <v-switch
                            :input-value="item.client.sendCodeBanAt"
                            :error="!!item.client.sendCodeBanAt"
                            readonly
                            @click="openDialog(item)"
                          ></v-switch>
                        </span>
                      </template>
                      <span>Запрет отправки СМС с кодом авторизации на сайте и мобильном приложении</span>
                    </v-tooltip>
                    <v-icon v-else-if="item.client.sendCodeBanAt" color="warning">
                      mdi-cancel
                    </v-icon>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <div v-else class="no-data-label">
              Нет записей за выбранный период
            </div>
          </div>
        </template>
      </v-container>
    </div>
    <primary-confirm-dialog
      v-model="showBanSMSDialog"
      :title="banSMSDialogTitle"
      :loading="banSMSLoading"
      close-after-loading
      @confirm="handleDialogConfirm"
    />
  </div>
</template>

<script>
  import StatTopFilter from '@/components/stat/StatTopFilter.vue'
  import Loader from '@/components/loader/Loader'
  import { mapGetters } from 'vuex'
  import SnackbarMessage from '@/mixins/snackbar-message'
  import permissions from '@/mixins/permissions'
  import clientStatisticsApi from '@/api/statistics/client'
  import smsApi from '@/api/client/sms'
  import { getFileNameFromContentDispositionHeader } from '@/util/axios'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import { debounce, sortBy } from 'lodash'

  export default {
    name: 'CustomerBase',
    components: { Loader, StatTopFilter },
    mixins: [permissions, SnackbarMessage],
    data () {
      return {
        loading: false,
        reportDownloading: false,
        showBanSMSDialog: false,
        banSMSLoading: false,
        banSMSDialogTitle: '',
        banSMSItem: null,
        footerProps: { 'items-per-page-options': [15, 25, 50, 100, -1] },
        search: '',
        startDate: null,
        endDate: null,
        filialIds: [],
        filialPreset: null,
        itemsLength: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        sortDesc: [],
        headers: [
          { text: '#', value: 'id', align: 'center', sortable: false },
          { text: 'Клиент', value: 'client', align: 'left', sortable: false },
          { text: 'Заказов', value: 'ordersCount', align: 'center' },
          { text: 'Самовывоз', value: 'pickupOrdersCount', align: 'center' },
          { text: 'Доставок', value: 'deliveryOrdersCount', align: 'center' },
          { text: 'Средний ЧЕК', value: 'avgBill', align: 'center' },
          { text: 'Сумма заказов', value: 'sumOrders', align: 'center' },
          { text: 'Дата последнего заказа', value: 'lastOrderAt', align: 'center' },
          { text: 'Дата регистрации', value: 'client', align: 'center', sortable: false },
          { text: 'Телефон', value: 'client', align: 'center', sortable: false },
          { text: 'Бан СМС', value: 'client', align: 'center', sortable: false },
        ],
        statisticData: [],
      }
    },
    computed: {
      ...mapGetters({
        globalFilialId: 'branches/getGlobalFilialId',
      }),
      canBanSMS () {
        return this.userCan(CRMPermission.ProhibitSendCode)
      },
      noteText () {
        return `Отображены клиенты совершавшие заказы в филиале с ${this.formatDate(this.startDate)} по ${this.formatDate(this.endDate)}`
      },
    },
    watch: {
      search: debounce(function () {
        const payload = {
          from: this.startDate,
          to: this.endDate,
          filialIds: this.filialIds,
          filialPreset: this.filialPreset,
          search: this.search,
          page: this.page,
          limit: this.itemsPerPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        }

        this.fetchData(payload)
      }, 500),
    },
    methods: {
      formatDate (date) {
        return new Date(date).toLocaleString(this.$i18n.locale, {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
        })
      },
      optionsChanged (options) {
        if (this.page !== options.page || this.itemsPerPage !== options.itemsPerPage) {
          return true
        }

        if (this.sortBy.length !== options.sortBy.length || this.sortBy.some(item => !options.sortBy.includes(item))) {
          return true
        }

        if (this.sortDesc.length !== options.sortDesc.length || this.sortDesc.some(item => !options.sortDesc.includes(item))) {
          return true
        }

        return false
      },
      loadItems (data) {
        if (!this.optionsChanged(data)) {
          return
        }

        this.itemsPerPage = data.itemsPerPage
        this.page = data.page
        this.sortBy = data.sortBy
        this.sortDesc = data.sortDesc

        const payload = {
          from: this.startDate,
          to: this.endDate,
          filialIds: this.filialIds,
          filialPreset: this.filialPreset,
          search: this.search,
          page: data.page,
          limit: data.itemsPerPage,
          sortBy: data.sortBy,
          sortDesc: data.sortDesc,
        }

        this.fetchData(payload)
      },
      applyFilter (filter) {
        this.startDate = filter.from
        this.endDate = filter.to
        this.filialIds = filter.filialId
        this.filialPreset = filter.filialPreset

        const payload = {
          from: filter.from,
          to: filter.to,
          filialIds: filter.filialId,
          filialPreset: filter.filialPreset,
          search: this.search,
          page: this.page,
          limit: this.itemsPerPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        }

        this.fetchData(payload)
      },
      async fetchData (data) {
        this.loading = true
        this.statisticData = []

        try {
          const response = await clientStatisticsApi.fetchClientsStat(data)
          this.statisticData = response.items
          this.itemsLength = response.meta.total
          this.itemsPerPage = response.meta.limit
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.loading = false
      },
      async downloadReport (filter) {
        this.reportDownloading = true

        const payload = {
          from: filter.from,
          to: filter.to,
          filialIds: filter.filialId,
          filialPreset: filter.filialPreset,
          search: this.search,
        }

        try {
          const response = await clientStatisticsApi.exportClientsStat(payload)
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(response.data)
          link.setAttribute('download', getFileNameFromContentDispositionHeader(response))
          document.body.appendChild(link)
          link.click()
          link.remove()
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.reportDownloading = false
      },
      openDialog (data) {
        const clientName = data.client.name ? data.client.name : `Клиент ID=${data.client.id}`

        this.showBanSMSDialog = true
        this.banSMSItem = data
        this.banSMSDialogTitle = `Вы уверены, что хотите ${data.client.sendCodeBanAt ? 'разрешить' : 'запретить'} отправку СМС с кодом авторизации на сайте и мобильном приложении для "${clientName}"?`
      },
      async handleDialogConfirm () {
        this.banSMSLoading = true

        try {
          this.banSMSItem.client.sendCodeBanAt
            ? await smsApi.enableSending(this.banSMSItem.client.id)
            : await smsApi.disableSending(this.banSMSItem.client.id)

          this.changeBanSMSStatus()
          this.banSMSItem = null
        } catch (e) {
          this.showErrorFromResponse(e)
        }

        this.banSMSLoading = false
      },
      changeBanSMSStatus () {
        const index = this.statisticData.findIndex(item => item.id === this.banSMSItem.id)

        this.statisticData[index].client.sendCodeBanAt = !this.banSMSItem.client.sendCodeBanAt
      },
    },
  }
</script>

<style lang="less" scoped>
@import "src/assets/styles/statistic.less";

.user-filter-search {
  @media (max-width: 1700px) {
    margin-top: 10px;
  }
}

.statistic-datatable {
  ::v-deep {
    .v-data-footer {
      display: flex;
      margin-right: 0;
    }
  }
}

.note {
  background-color: #FFF3D7;
  border-radius: 10px;
  padding: 15px 30px;
}
</style>
